import { LoadingState, Alert, Button } from './ui/Primitives';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { claimPhoneNumber, getAvailablePhoneNumbers } from '../services/phoneNumbersApi';
import './PhoneNumberSelector.css';

export function areaCodeFromPhoneNumber(value = '') {
  let digits = String(value).replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits.length === 10 ? digits.slice(0, 3) : '';
}

function numberLabel(number) {
  return number.friendly_name || number.phone_number;
}

export default function PhoneNumberSelector({
  defaultAreaCode = '',
  onClaimed,
}) {
  const suggestedAreaCode = useMemo(
    () => {
      const digits = String(defaultAreaCode).replace(/\D/g, '');
      return areaCodeFromPhoneNumber(defaultAreaCode) || (digits.length === 3 ? digits : '');
    },
    [defaultAreaCode],
  );
  const initialNumberType = suggestedAreaCode.length === 3 ? 'local' : 'tollFree';
  const [numberType, setNumberType] = useState(initialNumberType);
  const [areaCode, setAreaCode] = useState(suggestedAreaCode);
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [claimingNumber, setClaimingNumber] = useState('');
  const [error, setError] = useState('');

  const search = useCallback(async (type, requestedAreaCode = '') => {
    if (type === 'local' && requestedAreaCode.length !== 3) {
      setError('Enter a three digit area code.');
      return;
    }
    setLoading(true);
    setError('');
    setHasSearched(false);
    try {
      const result = await getAvailablePhoneNumbers(type === 'local' ? requestedAreaCode : '');
      setNumbers(result);
    } catch (requestError) {
      if (requestError.response?.status === 404) {
        setNumbers([]);
      } else {
        setError('We could not load available numbers. Try searching again.');
      }
    } finally {
      setHasSearched(true);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    search(initialNumberType, suggestedAreaCode);
  }, [initialNumberType, search, suggestedAreaCode]);

  function changeNumberType(type) {
    if (type === numberType) return;
    setNumberType(type);
    setNumbers([]);
    setHasSearched(false);
    setError('');
    if (type === 'tollFree') search('tollFree');
    if (type === 'local' && areaCode.length === 3) search('local', areaCode);
  }

  function updateAreaCode(event) {
    setAreaCode(event.target.value.replace(/\D/g, '').slice(0, 3));
  }

  async function chooseNumber(number) {
    setClaimingNumber(number.phone_number);
    setError('');
    try {
      await claimPhoneNumber(number);
    } catch (_requestError) {
      setError('That number could not be selected. Choose another number or try again.');
      setClaimingNumber('');
      return;
    }
    try {
      await onClaimed?.(number);
    } finally {
      setClaimingNumber('');
    }
  }

  return (
    <div className="phone-selector">
      <div className="phone-selector-type" role="tablist" aria-label="Phone number type">
        <button
          type="button"
          role="tab"
          aria-selected={numberType === 'local'}
          className={numberType === 'local' ? 'selected' : ''}
          onClick={() => changeNumberType('local')}
        >
          <strong>Local</strong>
          <span>Use a familiar area code</span>
          {suggestedAreaCode ? <small>Suggested</small> : null}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={numberType === 'tollFree'}
          className={numberType === 'tollFree' ? 'selected' : ''}
          onClick={() => changeNumberType('tollFree')}
        >
          <strong>Toll free</strong>
          <span>Give callers a national number</span>
        </button>
      </div>

      {numberType === 'local' ? (
        <form
          className="phone-selector-search"
          onSubmit={event => {
            event.preventDefault();
            search('local', areaCode);
          }}
        >
          <label htmlFor="onboarding-area-code">
            Area code
            <input
              id="onboarding-area-code"
              inputMode="numeric"
              autoComplete="tel-area-code"
              value={areaCode}
              onChange={updateAreaCode}
              placeholder="510"
              aria-describedby={suggestedAreaCode ? 'area-code-suggestion' : undefined}
            />
          </label>
          <Button type="submit" variant="secondary" disabled={loading || areaCode.length !== 3} loading={loading} loadingLabel={"Searching…"}>
            {'Search'}
          </Button>
          {suggestedAreaCode ? <small id="area-code-suggestion">Suggested from your main business phone.</small> : null}
        </form>
      ) : (
        <div className="phone-selector-search phone-selector-toll-free-search">
          <div><strong>Toll free numbers</strong><span>Available numbers are shown below.</span></div>
          <Button variant="secondary" onClick={() => search('tollFree')} disabled={loading} loading={loading} loadingLabel={"Searching…"}>
            {'Refresh numbers'}
          </Button>
        </div>
      )}

      {error ? <Alert tone="danger" title="Number selection needs attention">{error}</Alert> : null}

      <div className="phone-selector-results" aria-live="polite" aria-busy={loading}>
        {loading ? <LoadingState label="Finding available numbers…" /> : null}
        {!loading && numbers.map(number => (
          <article className="phone-selector-number" key={number.phone_number}>
            <div>
              <strong>{numberLabel(number)}</strong>
              {number.region ? <span>{number.region}</span> : null}
            </div>
            <Button
              onClick={() => chooseNumber(number)}
              disabled={Boolean(claimingNumber)}
              loading={claimingNumber === number.phone_number}
              loadingLabel="Selecting…"
              aria-label={`Choose ${numberLabel(number)}`}
            >
              Choose
            </Button>
          </article>
        ))}
        {!loading && hasSearched && !error && numbers.length === 0 ? (
          <div className="phone-selector-empty">
            <strong>No numbers found</strong>
            <span>{numberType === 'local' ? 'Try a nearby area code.' : 'Refresh to check again.'}</span>
          </div>
        ) : null}
      </div>
      <p className="phone-selector-footnote">You can manage this number after setup.</p>
    </div>
  );
}
